import { useLocation, useParams } from 'react-router-dom';

import { useOptimizedCollectionNotes } from '@/features/public/hooks/useOptimizedCollectionNotes';
import type { DisplayedCollectionSection } from '@/features/collections/types/collection.types';
import { useCollectionStructure } from './useCollectionsQuery';
import { useCollectionWithFirstPage } from '@/features/public/hooks/useCollectionWithFirstPage';

/**
 * Single read path for the open collection's displayed structure.
 *
 * The collection sidebar renders in two flows with different data sources:
 * the main app reads local Dexie data (collectionKeys.structure), public
 * /shared routes read remote Supabase data. Both are query-backed; this hook
 * picks the right one so components don't care which flow they're in.
 */
// Stable fallback: consumers feed the structure into effect/memo dependencies,
// so an empty result must keep the same identity across renders.
const EMPTY_STRUCTURE: DisplayedCollectionSection[] = [];

export function useDisplayedCollectionStructure(): {
  structure: DisplayedCollectionSection[];
  isLoading: boolean;
} {
  const isShared = useLocation().pathname.includes('/shared');
  const { collectionId } = useParams<{ collectionId: string }>();

  const localStructure = useCollectionStructure(isShared ? undefined : collectionId);
  const { data: publicCollectionData } = useCollectionWithFirstPage(
    isShared ? collectionId : undefined
  );
  const publicNotes = useOptimizedCollectionNotes(publicCollectionData?.collection, {
    enabled: isShared,
  });

  if (isShared) {
    return {
      structure: publicNotes.structure ?? EMPTY_STRUCTURE,
      isLoading: publicNotes.isMetadataLoading,
    };
  }

  return {
    structure: localStructure.data ?? EMPTY_STRUCTURE,
    isLoading: localStructure.isLoading,
  };
}
